"use client";

import styles from "./Sidebar.module.css";
import cn from "classnames";
import { ConfigProvider, Menu, MenuProps } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { IntlShape, useIntl } from "react-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { SidebarProps } from "./Sidebar.props";
import { AppNav } from "@/app/app-nav";
import { useWindowSize } from "@/hooks/window-size";

import IconPoolHistory from "@/public/icons/sidebar/icon-pool-history.svg";
import IconConnect from "@/public/icons/sidebar/icon-connect.svg";
import IconHistory from "@/public/icons/sidebar/icon-history.svg";
import IconPayments from "@/public/icons/sidebar/icon-payments.svg";
import IconSettings from "@/public/icons/sidebar/icon-settings.svg";
import IconUsers from "@/public/icons/sidebar/icon-users.svg";
import IconCreateUser from "@/public/icons/sidebar/icon-create-user.svg";
import IconFeePlans from "@/public/icons/sidebar/icon-percent.svg";
import IconCoinsRates from "@/public/icons/sidebar/icon-merge.svg";
import IconPpsState from "@/public/icons/sidebar/icon-bar-chart.svg";
import IconMonitoring from "@/public/icons/sidebar/icon-monitoring.svg";
import IconMain from "@/public/icons/sidebar/icon-home.svg";
import { useAppContext } from "@/context/AppContext";
import { buildPathWithCoin, getCoinFromSearchParams } from "@/app/utils";
import { AppConstants } from '@/app/constants';

type MenuItem = Required<MenuProps>["items"][number];
const SIDEBAR_VISIBLE_BREAKPOINT = 768;
const SIDEBAR_FIXED_TOP = "1rem";

export default function Sidebar({
  className,
  style,
  ...props
}: SidebarProps) {
  const intl = useIntl();
  const router = useRouter();
  const windowSize = useWindowSize();
  const appContext = useAppContext();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const hostRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [selectedPath, setSelectedPath] = useState(
    pathName.replaceAll("/", "")
    // replaceAllPolyfill(pathName, "/", "")
  );
  const [fixedMetrics, setFixedMetrics] = useState({
    left: 0,
    width: 0,
    height: 0,
  });
  const [isPinned, setIsPinned] = useState(false);

  const routeCoin =
    getCoinFromSearchParams(searchParams) ?? appContext.rememberedCoin;
  const buildNavPath = useCallback(
    (path: string) => buildPathWithCoin(`/${path}`, undefined, routeCoin),
    [routeCoin]
  );
  const handleMenuClick = useCallback((path: string) => {
    router.push(buildNavPath(path));
  }, [buildNavPath, router]);

  const menuItems = useMemo(
    () =>
      provideMenuItems(
        intl,
        appContext.isAdmin || false,
        appContext.canObserveUsers == true,
        appContext.credentials?.isReadOnly,
        appContext.credentials?.login == AppConstants.SecondaryGuy,
        handleMenuClick
      ),
    [
      appContext.canObserveUsers,
      appContext.credentials?.isReadOnly,
      appContext.credentials?.login,
      appContext.isAdmin,
      handleMenuClick,
      intl,
    ]
  );
  const isSidebarVisible =
    (windowSize && windowSize.width && windowSize.width >= SIDEBAR_VISIBLE_BREAKPOINT) ||
    false;
  const isCollapsed =
    (windowSize && windowSize.width && windowSize.width < 1024) || false;
  const getStickyTopPx = useCallback(() => {
    if (typeof window === "undefined") {
      return 0;
    }

    const rootFontSize = parseFloat(
      window.getComputedStyle(document.documentElement).fontSize || "16"
    );
    return parseFloat(SIDEBAR_FIXED_TOP) * rootFontSize;
  }, []);
  const isFixedReady = isSidebarVisible && fixedMetrics.width > 0;
  const measureFixedMetrics = useCallback(() => {
    const host = hostRef.current;
    const wrapper = wrapperRef.current;

    if (!isSidebarVisible || !host || !wrapper) {
      setFixedMetrics({
        left: 0,
        width: 0,
        height: 0,
      });
      setIsPinned(false);
      return;
    }

    const hostRect = host.getBoundingClientRect();
    const stickyTopPx = getStickyTopPx();
    setFixedMetrics({
      left: hostRect.left,
      width: hostRect.width,
      height: wrapper.scrollHeight,
    });
    setIsPinned(hostRect.top <= stickyTopPx);
  }, [getStickyTopPx, isSidebarVisible]);

  useEffect(() => {
    setSelectedPath(pathName.replaceAll("/", ""));
    // setSelectedPath(replaceAllPolyfill(pathName, "/", ""));
  }, [pathName]);

  useLayoutEffect(() => {
    if (!isSidebarVisible) {
      setFixedMetrics({
        left: 0,
        width: 0,
        height: 0,
      });
      setIsPinned(false);
      return;
    }

    let frameId = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measureFixedMetrics);
    };

    scheduleMeasure();

    const host = hostRef.current;
    const wrapper = wrapperRef.current;
    if (!host || !wrapper) {
      return () => cancelAnimationFrame(frameId);
    }

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleMeasure);
      observer.observe(host);
      observer.observe(wrapper);
    }

    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, { passive: true });
    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure);
    };
  }, [isCollapsed, isSidebarVisible, measureFixedMetrics, menuItems.length]);

  const hostStyle: CSSProperties | undefined = isFixedReady
    ? {
        ...style,
        minHeight: fixedMetrics.height ? `${fixedMetrics.height}px` : style?.minHeight,
      }
    : style;
  const wrapperStyle: CSSProperties | undefined = isFixedReady && isPinned
    ? {
        left: `${fixedMetrics.left}px`,
        width: `${fixedMetrics.width}px`,
        top: SIDEBAR_FIXED_TOP,
      }
    : undefined;

  return (
    <div
      {...props}
      ref={hostRef}
      style={hostStyle}
      className={cn(styles.host, className)}
    >
      <div
        ref={wrapperRef}
        style={wrapperStyle}
        className={cn(styles.wrapper, isFixedReady && isPinned && styles.wrapperFixed)}
      >
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                activeBarBorderWidth: 0,
              },
            },
          }}
        >
          <Menu
            defaultSelectedKeys={[AppNav.Main]}
            mode="inline"
            style={{ background: "var(--background)" }}
            inlineCollapsed={isCollapsed}
            selectedKeys={[selectedPath]}
            items={menuItems}
          />
        </ConfigProvider>
      </div>
    </div>
  );
}

function provideMenuItems(
  intl: IntlShape,
  hasPrivilegedAccess: boolean,
  canObserveUsers: boolean,
  isReadOnly: boolean | undefined,
  isSecondaryGuy: boolean,
  handleMenuClick: (path: string) => void
): MenuItem[] {
  const result: MenuItem[] = [];

  result.push({
    key: AppNav.Main,
    icon: <IconMain />,
    label: intl.messages["nav-main"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.HistoryPool,
    icon: <IconPoolHistory />,
    label: intl.messages["nav-pool-history"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  // result.push({
  //   key: AppNav.Rewards,
  //   icon: <IconInfo />,
  //   label: intl.messages["nav-rewards"] as string,
  //   onClick: (e) => handleMenuClick(e.key),
  // });
  result.push({
    key: AppNav.Connect,
    icon: <IconConnect />,
    label: intl.messages["nav-connect"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.Monitoring,
    icon: <IconMonitoring />,
    label: intl.messages["nav-monitoring"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.History,
    icon: <IconHistory />,
    label: intl.messages["nav-history"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });
  result.push({
    key: AppNav.Payouts,
    icon: <IconPayments />,
    label: intl.messages["nav-payouts"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });

  if (isSecondaryGuy || isReadOnly != undefined && !isReadOnly) {
    result.push({
      key: AppNav.Settings,
      icon: <IconSettings />,
      label: intl.messages["nav-settings"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  result.push({
    key: AppNav.About,
    icon: <InfoCircleOutlined />,
    label: intl.messages["nav-about"] as string,
    onClick: (e) => handleMenuClick(e.key),
  });

  // result.push({
  //   key: AppNav.Settings,
  //   icon: <IconSettings />,
  //   label: intl.messages["nav-settings"] as string,
  //   onClick: (e) => handleMenuClick(e.key),
  // });
  if (hasPrivilegedAccess || canObserveUsers) {
    result.push({
      type: "divider",
    });
    result.push({
      key: AppNav.Users,
      icon: <IconUsers />,
      label: intl.messages["nav-users"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  if (!hasPrivilegedAccess && canObserveUsers) {
    result.push({
      key: AppNav.FeePlan,
      icon: <IconFeePlans />,
      label: intl.messages["nav-fee-plans"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  if (hasPrivilegedAccess) {
    const createUserDisabled = isReadOnly == true;
    const feePlanDisabled = isSecondaryGuy;

    result.push({
      key: AppNav.CreateUser,
      icon: <IconCreateUser />,
      label: intl.messages["nav-create-user"] as string,
      disabled: createUserDisabled,
      onClick: createUserDisabled ? undefined : (e) => handleMenuClick(e.key),
    });
    result.push({
      key: AppNav.FeePlan,
      icon: <IconFeePlans />,
      label: intl.messages["nav-fee-plans"] as string,
      disabled: feePlanDisabled,
      onClick: feePlanDisabled ? undefined : (e) => handleMenuClick(e.key),
    });
    result.push({
      key: AppNav.ProfitSettings,
      icon: <IconCoinsRates />,
      label: intl.messages["nav-profit-settings"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
    result.push({
      key: AppNav.PpsState,
      icon: <IconPpsState />,
      label: intl.messages["nav-pps-state"] as string,
      onClick: (e) => handleMenuClick(e.key),
    });
  }

  return result;
}
